'use client';

import { useEffect, useState } from 'react';
import { CHUNK_RELOAD_KEY, isChunkLoadError } from '@/libs/chunk-load-error';

const COLORS = {
	background: '#F7F7F7',
	card: '#FFFFFF',
	text: '#171717',
	textMuted: '#727475',
	brandBar: '#000000',
	primary: '#F20094',
	primaryHover: '#BD0073',
	border: '#E1E3E5',
};

const ErrorIcon = () => (
	<svg
		width='56'
		height='56'
		viewBox='0 0 56 56'
		fill='none'
		xmlns='http://www.w3.org/2000/svg'
		aria-hidden='true'>
		<circle
			cx='28'
			cy='28'
			r='28'
			fill='#FFF0F9'
		/>
		<path
			d='M28 16v16M28 38v2'
			stroke={COLORS.primary}
			strokeWidth='3'
			strokeLinecap='round'
		/>
	</svg>
);

const GlobalError = ({ error, reset }) => {
	const [showError, setShowError] = useState(() => !isChunkLoadError(error));

	useEffect(() => {
		if (!isChunkLoadError(error)) {
			setShowError(true);
			return;
		}

		try {
			if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
				setShowError(true);
				return;
			}

			sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
		} catch {
			setShowError(true);
			return;
		}

		window.location.reload();
	}, [error]);

	const retry = () => {
		if (isChunkLoadError(error)) {
			window.location.reload();
			return;
		}

		reset();
	};

	const sendErrorToBackend = async () => {
		await fetch(`${process.env.APP_API_SERVER}/report-error`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				message: error.message,
				stack: error.stack,
				digest: error.digest,
			}),
		});

		alert('Error reported. Thank you!');

		retry();
	};

	if (!showError) return null;

	return (
		<html lang='en'>
			<body
				style={{
					margin: 0,
					padding: 0,
					fontFamily:
						"'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
					backgroundColor: COLORS.background,
					color: COLORS.text,
					WebkitFontSmoothing: 'antialiased',
				}}>
				<div
					style={{
						minHeight: '100vh',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						padding: '24px',
						boxSizing: 'border-box',
					}}>
					<div
						style={{
							width: '100%',
							maxWidth: '480px',
							backgroundColor: COLORS.card,
							borderRadius: '16px',
							border: `1px solid ${COLORS.border}`,
							boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
							overflow: 'hidden',
						}}>
						<div
							style={{
								backgroundColor: COLORS.brandBar,
								padding: '20px 32px',
								display: 'flex',
								justifyContent: 'center',
							}}>
							<img
								src='/images/svg/whiteLogo.svg'
								alt='Murakami City'
								style={{ height: '32px', width: 'auto' }}
							/>
						</div>

						<div
							style={{
								padding: '40px 32px 32px',
								textAlign: 'center',
							}}>
							<div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
								<ErrorIcon />
							</div>

							<h1
								style={{
									margin: '0 0 12px',
									fontSize: '1.5rem',
									fontWeight: 600,
									lineHeight: 1.3,
									color: COLORS.text,
								}}>
								Something went wrong
							</h1>

							<p
								style={{
									margin: '0 0 32px',
									fontSize: '0.9375rem',
									lineHeight: 1.6,
									color: COLORS.textMuted,
								}}>
								We&apos;re sorry for the inconvenience. Our team has been notified and is working on a
								fix. You can try again or report the problem to help us improve.
							</p>

							<div
								style={{
									display: 'flex',
									flexDirection: 'column',
									gap: '12px',
								}}>
								<button
									type='button'
									onClick={retry}
									style={{
										padding: '14px 24px',
										fontSize: '0.9375rem',
										fontWeight: 500,
										fontFamily: 'inherit',
										borderRadius: '999px',
										border: 'none',
										cursor: 'pointer',
										color: '#FFFFFF',
										backgroundColor: COLORS.primary,
									}}
									onMouseOver={(e) => {
										e.currentTarget.style.backgroundColor = COLORS.primaryHover;
									}}
									onMouseOut={(e) => {
										e.currentTarget.style.backgroundColor = COLORS.primary;
									}}>
									Try again
								</button>

								<button
									type='button'
									onClick={sendErrorToBackend}
									style={{
										padding: '14px 24px',
										fontSize: '0.9375rem',
										fontWeight: 500,
										fontFamily: 'inherit',
										borderRadius: '999px',
										border: `1px solid ${COLORS.border}`,
										cursor: 'pointer',
										color: COLORS.text,
										backgroundColor: 'transparent',
									}}
									onMouseOver={(e) => {
										e.currentTarget.style.borderColor = COLORS.textMuted;
									}}
									onMouseOut={(e) => {
										e.currentTarget.style.borderColor = COLORS.border;
									}}>
									Report this error
								</button>
							</div>
						</div>
					</div>
				</div>
			</body>
		</html>
	);
};

export default GlobalError;
